import React, { useState } from 'react';
import { XIcon, CreditCardIcon, WalletIcon, SmartphoneIcon, CheckCircleIcon } from 'lucide-react';
interface AddFundsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (amount: number, paymentMethod: string) => void;
}
export function AddFundsModal({
  isOpen,
  onClose,
  onConfirm
}: AddFundsModalProps) {
  const [amount, setAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const quickAmounts = [50, 100, 200, 500];
  const paymentMethods = [{
    id: 'card',
    name: 'Credit/Debit Card',
    icon: <CreditCardIcon className="w-5 h-5" data-id="element-161" data-mp="15v4jq6qr5s70" />,
    description: 'Visa, Mastercard'
  }, {
    id: 'mada',
    name: 'Mada',
    icon: <CreditCardIcon className="w-5 h-5" data-id="element-162" data-mp="10wb7sir4698y" />,
    description: 'Saudi Payment Network'
  }, {
    id: 'apple-pay',
    name: 'Apple Pay',
    icon: <WalletIcon className="w-5 h-5" data-id="element-163" data-mp="32nen3lby8bzu" />,
    description: 'Fast & Secure'
  }, {
    id: 'stc-pay',
    name: 'STC Pay',
    icon: <SmartphoneIcon className="w-5 h-5" data-id="element-164" data-mp="2s1ym40npg8ex" />,
    description: 'Mobile Wallet'
  }];
  const handleConfirm = () => {
    if (amount && selectedPaymentMethod) {
      onConfirm(parseFloat(amount), selectedPaymentMethod);
      setAmount('');
      setSelectedPaymentMethod('');
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-165" data-mp="36r4ofxhuaer1">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" data-id="element-166" data-mp="3fpzrx1ti1aqn">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-167" data-mp="3kpcermkdeht8">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-168" data-mp="3po5qb9g3jyqr">Add Funds</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-169" data-mp="3pne5mto1flf9">
            <XIcon className="w-5 h-5 text-gray-500" data-id="element-170" data-mp="1v9v6jfeczuru" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-171" data-mp="1n8x9vf1txzla">
          {/* Amount Input */}
          <div data-id="element-172" data-mp="1iadkf4o125o6">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-173" data-mp="1iavouokufq76">
              Enter Amount
            </label>
            <div className="relative" data-id="element-174" data-mp="1x8o0v6r6lwgm">
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium" data-id="element-175" data-mp="1x5jrluyv0aj3">
                SAR
              </span>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" className="w-full pl-16 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] text-lg font-medium" data-id="element-176" data-mp="267jc4x931wqs" />
            </div>
          </div>
          {/* Quick Amount Buttons */}
          <div data-id="element-177" data-mp="2ozc20isuv1ua">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-178" data-mp="2oz2ed1va96rr">
              Quick Select
            </label>
            <div className="grid grid-cols-4 gap-2" data-id="element-179" data-mp="33lhrrr7m1wxq">
              {quickAmounts.map(quickAmount => <button key={quickAmount} onClick={() => setAmount(quickAmount.toString())} className={`py-2 px-3 rounded-lg border-2 font-medium transition-all ${amount === quickAmount.toString() ? 'border-[#1E2B55] bg-blue-50 text-[#1E2B55]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-180" data-mp="38kcakt5kvlhu">
                  {quickAmount}
                </button>)}
            </div>
          </div>
          {/* Payment Methods */}
          <div data-id="element-181" data-mp="2bulewqljvsag">
            <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-182" data-mp="2bv4mbddkqya8">
              Payment Method
            </label>
            <div className="space-y-2" data-id="element-183" data-mp="2fwq49d4yu2ra">
              {paymentMethods.map(method => <button key={method.id} onClick={() => setSelectedPaymentMethod(method.id)} className={`w-full p-4 rounded-lg border-2 transition-all flex items-center justify-between ${selectedPaymentMethod === method.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-184" data-mp="21xgr6mc23orj">
                  <div className="flex items-center gap-3" data-id="element-185" data-mp="30ftbdrqclrng">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedPaymentMethod === method.id ? 'bg-[#1E2B55] text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-186" data-mp="30e8fnysas2o0">
                      {method.icon}
                    </div>
                    <div className="text-left" data-id="element-187" data-mp="2ll6q0t8aevbu">
                      <p className="font-medium text-gray-900" data-id="element-188" data-mp="2hgp70w9r4m24">{method.name}</p>
                      <p className="text-xs text-gray-500" data-id="element-189" data-mp="2cinoccg8mor6">
                        {method.description}
                      </p>
                    </div>
                  </div>
                  {selectedPaymentMethod === method.id && <CheckCircleIcon className="w-6 h-6 text-[#1E2B55]" data-id="element-190" data-mp="asc6yrch2vtq" />}
                </button>)}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl" data-id="element-191" data-mp="n8bu319b9e11">
          <button onClick={handleConfirm} disabled={!amount || !selectedPaymentMethod} className="w-full bg-[#1E2B55] text-white font-bold py-3 rounded-lg hover:bg-[#162041] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-192" data-mp="s1eh0qjl1p89">
            Add SAR {amount || '0.00'}
          </button>
        </div>
      </div>
    </div>;
}